<template>
  <div v-bind:class="isInNewMode ? 'consumable-details':''">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column column is-full">
            <div class="box">
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
                <div v-if="!isEditMode" class="column is-narrow">
                  <b-button class="button" @click="backClicked" icon-left="arrow-left-bold" tabindex="1">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('ConsumableDetails', this.UserActions.UPDATE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" @click="editClicked" icon-left="pencil" tabindex="2">
                    {{ $t('common.edit') }}
                  </b-button>
                </div>
                <div v-if="!isEditMode && ((!this.is_template && currentUserAccess('ConsumableDetails', this.UserActions.DELETE, this.project_id)) 
                                          || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.DELETE)))"
                     class="column is-narrow">
                  <b-button class="button is-primary" type="is-danger" @click="deleteClicked" icon-left="delete"
                            tabindex="3">
                    {{ $t('common.delete') }}
                  </b-button>
                </div>
              </div>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-half">
                  <b-field :label="$t('consumable-details-view.consumable-resource-field')">
                    <b-taginput
                        ref="resourceInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedResources"
                        :data="filteredResources"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="true"
                        autocomplete
                        :remove-on-keys=[]
                        :placeholder="$t('consumable-details-view.consumable-resource-placeholder')"
                        tabindex="11"
                        @typing="getFilteredResources"
                        @input="onResourcesInput"
                        @blur="onResourceInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedResources[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('consumable-details-view.consumable-category-field')">
                    <b-taginput
                        ref="categoryInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedCategories"
                        :data="filteredCategories"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="true"
                        autocomplete
                        :remove-on-keys=[]
                        :placeholder="$t('consumable-details-view.consumable-category-placeholder')"
                        tabindex="12"
                        @typing="getFilteredCategories"
                        @input="onCategoriesInput"
                        @blur="onCategoryInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedCategories[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('consumable-details-view.consumable-provider-field')">
                    <b-taginput
                        ref="providerInput"
                        v-if="isEditMode"
                        maxtags="1"
                        v-model="selectedProviders"
                        :data="filteredProviders"
                        :loading="isFetchingCategories"
                        :has-counter="false"
                        :open-on-focus="true"
                        :allow-new="true"
                        autocomplete
                        :remove-on-keys=[]
                        :placeholder="$t('consumable-details-view.consumable-provider-placeholder')"
                        tabindex="13"
                        @typing="getFilteredProviders"
                        @input="onProvidersInput"
                        @blur="onProviderInputBlur">
                    </b-taginput>
                    <b-taglist v-else>
                      <b-tag type="is-info is-light" size="is-medium">
                        {{ selectedProviders[0] }}
                      </b-tag>
                    </b-taglist>
                  </b-field>
                  <b-field :label="$t('consumable-details-view.consumable-text-field')">
                    <b-input v-if="isEditMode" v-model="consumableData.text"
                             :placeholder="$t('consumable-details-view.consumable-text-placeholder')" tabindex="14"
                             type="textarea"></b-input>
                    <label v-else style="white-space: pre-wrap;">{{ consumableData.text }}</label>
                  </b-field>
                </div>
                <div class="column">
                  <b-field :label="$t('consumable-details-view.consumable-quantity-field')">
                    <div class="rows">
                      <div v-if="isEditMode" class="columns">
                        <b-input v-model="consumableData.meta.estimation.quantity"
                                 class="column" type="number" min=0 step="any"
                                 :placeholder="$t('consumable-details-view.consumable-quantity-placeholder')"
                                 tabindex="16"></b-input>
                        <b-select v-model="consumableData.unit"
                                  class="column" tabindex="17"
                                  :placeholder="$t('consumable-details-view.consumable-quantityunit-placeholder')">
                          <option
                              v-for="option in quantityUnits"
                              :value="option.id"
                              :key="option.id">
                            {{ $t(option.translationKey) }}
                          </option>
                        </b-select>
                      </div>
                      <label v-else>{{ getFormattedQuantity(consumableData.meta.estimation, consumableData.unit) }}
                      </label>
                    </div>
                  </b-field>
                  <b-field :label="$t('consumable-details-view.consumable-fee-per-unit-field')">
                    <div v-if="isEditMode" class="columns">
                      <b-input class="column estimation-value" v-model="consumableData.meta.estimation.fee_per_unit"
                               :placeholder="$t('consumable-details-view.consumable-fee-per-unit-placeholder')"
                               type="number" min=0 step="any" tabindex="18"></b-input>
                      <label class="column">{{ this.defaultValues.priceUnit }}</label>
                    </div>
                    <label v-else>{{ getFormattedFeePerUnit(consumableData.meta.estimation) }}</label>
                  </b-field>
                  <label v-if="isEditMode && invalidEstimation"
                         class="required-field">{{ $t('validation.equipment-details') }}
                  </label>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px" v-if="isEditMode">
                <div class="column is-narrow">
                  <b-button class="button" @click="cancelClicked" icon-left="close-thick" tabindex="31">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button v-if="((!this.is_template && currentUserAccess('ConsumableDetails', this.UserActions.UPDATE, this.project_id))
                                                 || (this.is_template && currentUserAccess('ProjectTemplates', this.UserActions.UPDATE)))"
                            class="button is-primary" @click="saveClicked" icon-left="content-save" tabindex="32">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "ConsumableDetails",
  props: {
    'project_id': {
      type: String,
    },
    'consumable_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      consumableData: {
        meta: {},
        unit: ""
      },
      isLoading: false,
      isEditMode: false,
      invalidEstimation: false,
      isFetchingCategories: false,
      categoricalData: {},
      filteredResources: [],
      selectedResources: [],
      filteredCategories: [],
      selectedCategories: [],
      filteredProviders: [],
      selectedProviders: []
    }
  },
  methods: {
    readConsumableData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const getConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getConsumableFunction({
        action: "get_consumable_by_id",
        project_id: this.project_id,
        document_id: this.consumable_id,
        fill_meta: true
      }).then((result) => {
        this.isLoading = false;
        console.log("read consumable result: ", result.data);
        this.consumableData = result.data["consumables"];
        if (!this.consumableData.meta.estimation)
          this.consumableData.meta.estimation = {};
        const resource = this.consumableData.resource
        if (resource !== undefined && resource !== "") {
          this.selectedResources[0] = resource
        }
        const category = this.consumableData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
        const provider = this.consumableData.provider
        if (provider !== undefined && provider !== "") {
          this.selectedProviders[0] = provider
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting consumables: ", error);
      });
    },
    createConsumable: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const insertConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_consumable";
      insertConsumableFunction(updateData).then((result) => {
        this.isLoading = false
        console.log("create consumable result: ", result.data);
        this.showSuccess(this.$t('messages.consumable-created'));
        this.$emit('consumableAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(error.message)
      });
    },
    updateConsumable: function (updateData) {
      this.isLoading = true;
      console.log(this.consumableData);
      const functions = firebase.app().functions("europe-west3");
      const updateConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_consumable";
      updateConsumableFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("result: ", result.data);
        this.showSuccess(this.$t('messages.consumable-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error updating consumable: ", error);
      });
    },
    deleteConsumable: function () {
      this.isLoading = true;
      console.log(this.consumableData);
      const functions = firebase.app().functions("europe-west3");
      const deleteConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteConsumableFunction({
        action: "delete_consumable",
        project_id: this.project_id,
        document_id: this.consumable_id,
      }).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("delete consumable result: ", result.data);
        this.showSuccess(this.$t('messages.consumable-deleted'));
        this.$emit('consumableDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error deleting consumable: ", error);
      });
    },
    readCategoricalData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions("europe-west3");
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["consumables"]
      }).then((result) => {
        this.isFetchingCategories = false
        console.log("read categories result: ", result.data);
        this.categoricalData = result.data["consumables"]
        this.filteredResources = this.categoricalData.resource
        this.filteredCategories = this.categoricalData.category
        this.filteredProviders = this.categoricalData.provider
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories: ", error);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    deleteClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('consumable-details-view.delete-consumable-confirm-title').toString(),
        message: this.$t('consumable-details-view.delete-consumable-confirm-message').toString(),
        confirmText: this.$t('consumable-details-view.delete-consumable-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        hasIcon: true,
        icon: 'delete',
        onConfirm: () => {
          this.deleteConsumable();
        }
      });
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readConsumableData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back()
    },
    saveClicked: function () {
      const dataFieldsToUpdate = {
        project_id: this.project_id,
        text: this.consumableData.text,
        estimation: {}
      }
      if (this.selectedResources[0] !== undefined) {
        dataFieldsToUpdate.resource = this.selectedResources[0]
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (this.selectedProviders[0] !== undefined) {
        dataFieldsToUpdate.provider = this.selectedProviders[0]
      }
      let estimation = this.consumableData.meta.estimation;
      if (estimation.quantity == null || estimation.quantity === "" || estimation.quantity === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.quantity = parseFloat(this.consumableData.meta.estimation.quantity);
        if (this.consumableData.unit == null || this.consumableData.unit === "" || this.consumableData.unit === undefined) {
          this.consumableData.unit = this.defaultValues.quantityUnitId;
        }
        dataFieldsToUpdate.unit = this.consumableData.unit;
      }
      if (estimation.fee_per_unit == null || estimation.fee_per_unit === "" || estimation.fee_per_unit === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.fee_per_unit = parseFloat(this.consumableData.meta.estimation.fee_per_unit);
      }

      if (this.isInNewMode) {
        this.createConsumable(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.document_id = this.consumable_id;
        this.updateConsumable(dataFieldsToUpdate);
      }
    },
    onResourcesInput() {
      this.filteredResources = this.categoricalData.resource
    },
    getFilteredResources(text) {
      this.filteredResources = this.categoricalData.resource.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoricalData.category
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoricalData.category.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onProvidersInput() {
      this.filteredProviders = this.categoricalData.provider
    },
    getFilteredProviders(text) {
      this.filteredProviders = this.categoricalData.provider.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onResourceInputBlur() {
      this.$refs.resourceInput.addTag()
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.addTag()
    },
    onProviderInputBlur() {
      this.$refs.providerInput.addTag()
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("ConsumableDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readCategoricalData()
    if (this.isInNewMode) {
      console.log("creating new consumable");
      this.consumableData.meta = {};
      this.consumableData.meta.estimation = {};
      this.isEditMode = true;
    } else {
      console.log("edit consumable : ", this.consumable_id);
      this.isEditMode = false;
      this.readConsumableData();
    }
  },
  computed: {
    isInNewMode() {
      return this.consumable_id === "new";
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('consumable-details-view.new-consumable-title')
      } else if (this.isEditMode) {
        return this.$t('consumable-details-view.edit-consumable-title') + " " + this.getAdjustedTitle(this.consumableData.resource)
      } else {
        return this.$t('consumable-details-view.view-consumable-title') + " " + this.getAdjustedTitle(this.consumableData.resource)
      }
    }
  }
};
</script>

<style scoped>
.estimation-value {
  max-width: 50%;
}

.required-field {
  color: red;
  font-size: 10pt;
}
</style>
